import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../api/api";
import { toast } from "react-toastify";

export const AddProduct = () => {
  const navigate = useNavigate();

  // form state
  const [name, setName] = useState("");
  const [category, setCategory] = useState(""); 
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [stockQty, setStockQty] = useState("");
  const [weight, setWeight] = useState(""); // optional
  const [visibility, setVisibility] = useState("local"); // required: local | global
  const [productCode, setProductCode] = useState("");
  const [productType, setProductType] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [sellerPhone, setSellerPhone] = useState("");

  // categories from backend
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingStoreInfo, setLoadingStoreInfo] = useState(true);
  const [storeName, setStoreName] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [catsError, setCatsError] = useState("");
  const [showStorePopup, setShowStorePopup] = useState(false);

  // media state (max 5 items)
  const [files, setFiles] = useState([]); 
  const inputRef = useRef(null);

  const handleFetchStore = async () => {
      try {
        setLoadingStoreInfo(true);
        const res = await api.business.getStore();
  
        if (res?.shop) {
          const shop = res.shop;
          setStoreName(shop.storeName || "");
          setSellerPhone(shop.phoneNumber || "");
          
        }
      } catch (error) {
        console.log("Error fetching store:", error);
      } finally {
        setLoadingStoreInfo(false);
      }
    };
  
    useEffect(() => {
      handleFetchStore();
    }, []);

    useEffect(() => {
  const hasStore = localStorage.getItem("hasStore");

  if (!hasStore) {
    setShowStorePopup(true);
  }
}, []);


  useEffect(() => {
    (async () => {
      try {
        setLoadingCats(true);
        setCatsError("");
        //  api.category.list() returns the raw array 
        console.log("api keys:", Object.keys(api)); 
        console.log("has list:", typeof api.categories?.list);
        const cats = await api.categories.list();
        setCategories(Array.isArray(cats) ? cats : []);
      } catch (err) {
        console.error("Failed to load categories:", err?.message || err);
        setCatsError(err?.message || "Failed to load categories");
      } finally {
        setLoadingCats(false);
      }
    })();
  }, []);
  

  const onPick = () => inputRef.current?.click();

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;

    const remaining = 5 - files.length;
    const toAdd = list.slice(0, remaining).map((f, idx) => {
      const sizeKB = Math.max(1, Math.round(f.size / 1024));
      return {
        id: `${Date.now()}_${idx}`,
        name: f.name,
        sizeKB,
        src: URL.createObjectURL(f),
        progress: 0,
        file: f,
      };
    });

    const updated = [...files, ...toAdd].slice(0, 5);
    setFiles(updated);

    // progress UI
    toAdd.forEach((fileObj, i) => {
      let p = i === toAdd.length - 1 ? 45 : 100;
      setTimeout(() => {
        setFiles((prev) =>
          prev.map((it) => (it.id === fileObj.id ? { ...it, progress: p } : it))
        );
      }, 200);
      setTimeout(() => {
        setFiles((prev) =>
          prev.map((it) => (it.id === fileObj.id ? { ...it, progress: 100 } : it))
        );
      }, 1600 + i * 300);
    });

    e.target.value = "";
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Must have name, category, visibility, price, stock, and >=3 images
  const canPublish = useMemo(() => {
    const validCore =
      name.trim() &&
      category &&
      (visibility === "local" || visibility === "global") &&
      String(price).trim() &&
      String(stockQty).trim();

    const realFiles = files.filter((f) => !!f.file);
    return !!validCore && realFiles.length >= 3;
  }, [name, category, visibility, price, stockQty, files]);

  const publish = async (e) => {
    e.preventDefault();
    if (!canPublish) {
      toast.warn("Please complete all required fields and add at least 3 images.");
      return;
    }

    // Prepare payload
    const images = files.filter((f) => !!f.file).map((f) => f.file);

    // Basic client-side numeric validation
    const priceNum = Number(price);
    const stockNum = Number(stockQty);
    if (isNaN(priceNum) || priceNum < 0) return toast.warn("Price must be a non-negative number.");
    if (isNaN(stockNum) || stockNum < 0) return toast.warn("Stock quantity must be a non-negative number.");

    const payload = {
      name: name.trim(),
      stockQuantity: stockNum,
      pricePerUnit: priceNum,
      weight: weight === "" ? undefined : Number(weight),
      category, // ObjectId
      visibility,
      description: desc.trim(),
      productType: productType || undefined,
      discountPercent: discount === "" ? undefined : Number(discount),
      images,
      sellerName: storeName.trim() || undefined, 
      sellerPhone: sellerPhone || undefined,
    };

    try {
      setSubmitLoading(true);
      const created = await api.products.create(payload);
      if (!created?.product && created?.message) {
        throw { message: created.message };
      }
      // Success → go next (choose your flow)
      toast.success("Product created successfully")
      // e.g., navigate to delivery setup or to your products list
      // navigate("/setup/delivery");
      // show a toast/snackbar here
      console.log("Product created:", created);
    } catch (err) {
      console.error("Create product failed:", err?.status, err?.message, err?.data);
      const msg =
        err?.data?.message ||
        err?.data?.error ||
        err?.message ||
        "Failed to create product";
      toast.error(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      {showStorePopup && (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
    <div className="bg-white w-[90%] rounded-2xl p-6 shadow-lg text-center">
      <h2 className="text-xl font-bold text-[#5d4037]">Set Up Your Store</h2>
      <p className="mt-2 text-[14px] text-[#5d4037]/70">
        You need to set up your store before you can add products.
      </p>

      <button
        onClick={() => navigate("/personal-info")}
        className="mt-4 w-full bg-[#ccb357] text-[#5d4037] font-semibold py-3 rounded-xl"
      >
        Setup Store
      </button>

      {/* <button
        onClick={() => setShowStorePopup(false)}
        className="mt-3 w-full text-[#5d4037] underline"
      >
        Cancel
      </button> */}
    </div>
  </div>
)}


      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-16">
        {/* Top bar */}
        <div className="w-full flex items-center mt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center"
            aria-label="Back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">Add Product</h1>
          <div className="w-9" />
        </div>

        {/* Subtitle */}
        <p className="mt-2 px-4 text-[14px]">
          To start selling, all you need is a name, price and images (min 3).
        </p>

        {/* Section title */}
        <p className="mt-4 px-4 font-extrabold uppercase text-[13px] tracking-wide">
          Product Details
        </p>

        <form onSubmit={publish} className="mt-2 px-4">
          {/* Product Name */}
          <label className="block text-base font-semibold mb-2">Product Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Product name"
            className="w-full h-[52px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] px-4 text-[#7a6f5c] outline-none"
            type="text"
          />
          <p className="mt-1 text-[12px] text-[#5d4037]/80">Give your product a short and clear name</p>

          {/* Category Selection  */}
          <label className="block mt-5 text-base font-semibold mb-2">Category</label>
          <div className="relative">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="appearance-none w-full h-[52px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] pl-4 pr-10 text-[#7a6f5c] outline-none"
              disabled={loadingCats || categories.length === 0}
            >
              {loadingCats && <option>Loading...</option>}
              {!loadingCats && categories.length === 0 && <option>No categories</option>}
              {!loadingCats &&
                categories.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
            </select>
            <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </div>
          <p className="mt-1 text-[12px] text-[#5d4037]/80">Choose the category for this product</p>

          {/* Seller details (used if profile lacks these) */}
          <label className="block mt-5 text-base font-semibold mb-2">Seller Name (optional)</label>
          <input
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            placeholder="e.g. Jane’s Kitchen"
            className="w-full h-[48px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] px-3 text-[#7a6f5c] outline-none"
            type="text"
          />

          <label className="block mt-3 text-base font-semibold mb-2">Seller Phone (optional)</label>
          <input
            value={sellerPhone}
            onChange={(e) => setSellerPhone(e.target.value)}
            placeholder="e.g. 0803 123 4567"
            className="w-full h-[48px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] px-3 text-[#7a6f5c] outline-none"
            type="tel"
            inputMode="tel"
          />
          <p className="mt-1 text-[12px] text-[#5d4037]/80">
            Used only if your profile phone is missing.
          </p>

          {/* Visibility */}
          <label className="block mt-5 text-base font-semibold mb-2">Visibility</label>
          <div className="relative">
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="appearance-none w-full h-[52px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] pl-4 pr-10 text-[#7a6f5c] outline-none"
            >
              <option value="local">Local</option>
              <option value="global">Global</option>
            </select>
            <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </div>

          {/* Product Description */}
          <label className="block mt-5 text-base font-semibold mb-2">Product Description</label>
          <textarea
            rows={4}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Type here"
            className="w-full bg-[#fff8e1] rounded-[14px] border border-[#5d4037] p-4 text-[#7a6f5c] outline-none"
          />
          <p className="mt-1 text-[12px] text-[#5d4037]/80">
            Give your product a short and clear description
          </p>

          {/* Pricing & Stock */}
          <p className="mt-6 font-extrabold uppercase text-[13px] tracking-wide">Pricing & Stock</p>
          <p className="mt-1 text-[12px] text-[#5d4037]/80">
            Set a price, stock quantity, and (optionally) discount.
          </p>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="sr-only">Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5d4037]/70">₦</span>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full h-[48px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] pl-7 pr-3 text-[#7a6f5c] outline-none"
                  type="text"
                  inputMode="decimal"
                />
              </div>
              <p className="mt-1 text-[12px] text-[#5d4037]/80">Price</p>
            </div>
            <div>
              <label className="sr-only">Discount (%)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5d4037]/70">%</span>
                <input
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="0"
                  className="w-full h-[48px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] pl-7 pr-3 text-[#7a6f5c] outline-none"
                  type="text"
                  inputMode="numeric"
                />
              </div>
              <p className="mt-1 text-[12px] text-[#5d4037]/80">Discount Percent (optional)</p>
            </div>
            <div>
              <input
                value={stockQty}
                onChange={(e) => setStockQty(e.target.value)}
                placeholder="Stock Qty"
                className="w-full h-[48px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] px-3 text-[#7a6f5c] outline-none"
                type="text"
                inputMode="numeric"
              />
            </div>
            <div>
              <input
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="Weight (optional)"
                className="w-full h-[48px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] px-3 text-[#7a6f5c] outline-none"
                type="text"
                inputMode="decimal"
              />
            </div>
          </div>

          {/* Product Type (optional enum) */}
          <label className="block mt-5 text-base font-semibold mb-2">Product Type (optional)</label>
          <div className="relative">
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              className="appearance-none w-full h-[48px] bg-[#fff8e1] rounded-[14px] border border-[#5d4037] pl-4 pr-10 text-[#7a6f5c] outline-none"
            >
              <option value="">None</option>
              <option value="top_selling">Top Selling</option>
              <option value="top_farmer">Top Farmer</option>
              <option value="flash_sale">Flash Sale</option>
            </select>
            <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </div>

          {/* Media */}
          <p className="mt-6 font-extrabold uppercase text-[13px] tracking-wide">Media</p>
          <p className="mt-1 text-[12px] text-[#5d4037]/80">
            Add up to 5 images. <strong>Minimum 3 required</strong>.
          </p>

          <button
            type="button"
            onClick={onPick}
            className="mt-3 w-full h-[72px] rounded-[14px] border-2 border-dashed border-[#5d4037] bg-[#f3e9bf] text-[#5d4037] grid place-items-center"
          >
            Upload your image
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
          />

          {files.length > 0 && (
            <div className="mt-4 space-y-3">
              <p className="text-[13px]">Uploads</p>
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded overflow-hidden border border-[#5d4037] bg-white">
                    <img src={f.src} alt={f.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] truncate max-w-[180px]">{f.name}</span>
                      <button type="button" onClick={() => removeFile(f.id)} aria-label="Remove">
                        <span className="text-[#5d4037] text-lg">×</span>
                      </button>
                    </div>
                    <div className="text-[11px] text-[#5d4037]/70">{f.sizeKB}KB</div>
                    {f.progress < 100 && (
                      <div className="mt-1 h-2 rounded bg-[#eadfb7]">
                        <div
                          className="h-2 rounded bg-[#5d4037]"
                          style={{ width: `${f.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Publish / Cancel */}
          <div className="mt-5 flex flex-col items-center">
            <button
              type="submit"
              disabled={!canPublish || submitLoading}
              className={`w-[210px] h-[48px] rounded-[24px] font-semibold text-[#5d4037] transition-opacity ${
                !canPublish || submitLoading
                  ? "bg-[#8f814f] opacity-80 cursor-not-allowed"
                  : "bg-[#f7c945] hover:opacity-90"
              }`}
            >
              {submitLoading ? "Publishing..." : "Publish"}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mt-3 text-[#c34040] underline"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Bottom bar & home indicator */}
        <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
          <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
            <div className="w-[120px] h-[6px] rounded-full bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
};
